import Swiper from 'swiper'
import { Navigation, Scrollbar, Autoplay, Thumbs, EffectFade } from 'swiper/modules'

/**
 * Master-fil för alla .swiper-carousel i temat.
 *
 * Konfigureras helt via data-attribut (alla är JSON):
 *
 *   data-slider_breakpoints   Swiper breakpoints.
 *   data-slider_autoplay      Närvaron aktiverar kontinuerlig autoplay (loop
 *                             utan stopp). Värde (valfritt):
 *                               { "speed": 6000 }   // ms per steg, lägre = snabbare
 *   data-slider_thumbs        Närvaron aktiverar koppling till en .swiper-thumbs
 *                             i samma parent. Värde (alla valfria):
 *                               { "target": ".swiper-thumbs",
 *                                 "effect": "fade" | "slide",
 *                                 "touch":  true | false }
 *
 * Tomt värde (t.ex. data-slider_thumbs='{}') = default-config.
 */

/**
 * Läser ett data-attribut och tolkar det som JSON.
 *   - attribut saknas helt   → null
 *   - attributet är tomt     → {}     (= aktivera med defaults)
 *   - giltig JSON            → parsat värde
 *   - ogiltig JSON           → null (loggar varning)
 */
function readJSON(el, key) {
    const raw = el.dataset[key]
    if (raw == null) return null
    try {
        return raw.trim() ? JSON.parse(raw) : {}
    } catch (err) {
        console.warn(`[sliders] Ogiltig JSON i data-${key}:`, raw, err)
        return null
    }
}

/**
 * Skapar en Swiper-instans på elementet, men bara om en inte redan finns.
 * Swiper sätter automatiskt el.swiper, så den används som idempotens-check
 * (skyddar mot dubbla init, t.ex. vid HMR).
 */
function makeSwiper(el, options) {
    if (el.swiper) return el.swiper
    el.swiper = new Swiper(el, options)
    return el.swiper
}

/**
 * Initierar en thumbs-slider (det lilla navigationsbandet).
 * Anropas av initCarousel när en master pekar ut den.
 */
function initThumbs(el) {
    return makeSwiper(el, {
        modules: [Navigation],
        freeMode: true,
        watchSlidesProgress: true,
        breakpoints: readJSON(el, 'slider_breakpoints') || undefined,
    })
}

/**
 * Initierar en master-slider (.swiper-carousel).
 * Bygger ihop Swiper-options baserat på data-attribut:
 * navigation/scrollbar hittas i parent, breakpoints/autoplay/thumbs läses
 * från data-attribut. Om data-slider_thumbs finns kopplas en thumbs-slider in.
 */
function initCarousel(el) {
    const parent = el.parentElement

    const options = {
        modules: [Navigation, Scrollbar, Autoplay],
        speed: 500,
        navigation: {
            nextEl: parent.querySelector('.swiper-button-next'),
            prevEl: parent.querySelector('.swiper-button-prev'),
        },
        scrollbar: {
            el: parent.querySelector('.swiper-scrollbar'),
            draggable: true,
        },
    }

    const breakpoints = readJSON(el, 'slider_breakpoints')
    if (breakpoints) options.breakpoints = breakpoints

    const autoplay = readJSON(el, 'slider_autoplay')
    if (autoplay) {
        const wrapperEl = el.querySelector('.swiper-wrapper')
        const originalSlides = wrapperEl ? Array.from(wrapperEl.children) : []
        const minRealSlides = 16
        if (originalSlides.length > 0 && originalSlides.length < minRealSlides) {
            const extraRounds = Math.ceil(minRealSlides / originalSlides.length) - 1
            for (let i = 0; i < extraRounds; i++) {
                originalSlides.forEach((slide) => wrapperEl.appendChild(slide.cloneNode(true)))
            }
        }

        options.loop = true
        // Förklonar fler slides utanför synfältet så att de kan "glida" in
        options.loopAdditionalSlides = 10
        options.speed = autoplay.speed || 6000
        options.autoplay = {
            delay: 0,
        }
    }

    const thumbs = readJSON(el, 'slider_thumbs')
    if (thumbs) {
        const thumbsEl = parent.querySelector(thumbs.target || '.swiper-thumbs')
        if (thumbsEl) {
            options.modules.push(Thumbs, EffectFade)
            options.thumbs = { swiper: initThumbs(thumbsEl) }
            if (thumbs.effect !== 'slide') {
                options.effect = 'fade'
                options.fadeEffect = { crossFade: true }
            }
            if (thumbs.touch !== true) {
                options.allowTouchMove = false
            }
        }
    }

    const swiperInstance = makeSwiper(el, options)

    if (autoplay) {
        initAutoplayToggle(parent, swiperInstance)
    }
}

/**
 * Pausknapp för autoplay.
 */
function initAutoplayToggle(parent, swiperInstance) {
    const toggleBtn = parent.querySelector('.swiper-autoplay-toggle')
    if (!toggleBtn || toggleBtn.dataset.bound) return
    toggleBtn.dataset.bound = '1'

    const pauseIcon = toggleBtn.querySelector('.swiper-autoplay-icon-pause')
    const playIcon = toggleBtn.querySelector('.swiper-autoplay-icon-play')

    toggleBtn.addEventListener('click', () => {
        const isPaused = toggleBtn.classList.toggle('is-paused')

        if (isPaused) {
            // autoplay.stop() hindrar bara NÄSTA steg från att schemaläggas —
            // den pågående CSS-övergången skulle annars glida klart till sin
            // slutposition. Läs av wrapperns exakta interpolerade position
            // just nu och frys den där utan övergång.
            swiperInstance.autoplay.stop()
            try {
                const matrix = new DOMMatrixReadOnly(getComputedStyle(swiperInstance.wrapperEl).transform)
                swiperInstance.setTransition(0)
                swiperInstance.setTranslate(matrix.m41)
                // Utan detta tror Swiper internt att den fortfarande animerar
                // (transitionend hann aldrig triggas eftersom vi avbröt
                // övergången manuellt) och vägrar då starta nästa steg när
                // autoplay återupptas.
                swiperInstance.transitionEnd()
            } catch (err) {
                // Om transformen inte gick att tolka: redan stoppad ovan,
                // den hinner bara glida klart det sista steget.
            }
        } else {
            swiperInstance.autoplay.start()
        }

        pauseIcon?.classList.toggle('hidden', isPaused)
        playIcon?.classList.toggle('hidden', !isPaused)
        toggleBtn.setAttribute(
            'aria-label',
            isPaused ? toggleBtn.dataset.pausedLabel || 'Spela upp' : toggleBtn.dataset.playingLabel || 'Pausa'
        )
    })
}

document.querySelectorAll('.swiper-carousel').forEach(initCarousel)
